import { ReactNode } from 'react';
import styled from 'styled-components';
import { Dialog as DialogBase } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ReactComponent as CloseIcon } from '../../assets/close.svg';
import ButtonBase, { LoadingButton, Props as ButtonProps } from '../Button';
import { Flex, FlexSpace, FlexColumn } from '../Flex';
import OverflowTooltip from '../OverflowTooltip';
import IconButton from '../IconButton';
import { componentToken } from '../../style/utils';

const Wrapper = styled(DialogBase)`
  & .MuiDialog-paper {
    min-width: 540px;
    width: ${({ $customWidth }: { $customWidth?: number }) =>
      $customWidth ? `${$customWidth}px` : ''};
    max-width: 100%;
  }
`;

const Title = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${componentToken('dialog.title.gap')};
  padding: ${componentToken('dialog.title.padding')};
  background-color: ${componentToken('dialog.title.backgroundColor')};
`;

const Content = styled(FlexColumn)`
  gap: ${componentToken('dialog.content.gap')};
  padding: ${componentToken('dialog.content.padding')};
  overflow: hidden;
  overflow-y: auto;
`;

const ButtonContainer = styled(Flex)`
  line-height: ${componentToken('dialog.buttonContainer.lineHeight')};
  text-align: center;
  gap: ${componentToken('dialog.buttonContainer.gap')};
`;

const Button = styled(ButtonBase)`
  margin-left: ${componentToken('dialog.button.marginLeft')};
`;

const Footer = styled(FlexSpace)<{ $showDivider?: boolean }>`
  justify-content: flex-end;
  gap: ${componentToken('dialog.footer.gap')};
  ${({ theme, $showDivider }) =>
    $showDivider &&
    `border-top: 1px solid ${theme.tokens.components.dialog.footer.borderTop};`}
  padding: ${componentToken('dialog.footer.padding')};
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onCancelButtonClick?: () => void;
  onConfirmButtonClick?: () => void;
  isLoading?: boolean;
  titleIcon?: ReactNode;
  title: string;
  titleChip?: ReactNode;
  exitButton?: boolean;
  message?: string | ReactNode;
  secondaryMessage?: string | ReactNode;
  children?: ReactNode;
  cancelText?: string;
  confirmText?: string;
  confirmDisabled?: boolean;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  graphic?: React.ReactNode;
  footerMessage?: React.ReactNode;
  footerRight?: React.ReactNode;
  footerDivider?: boolean;
  customWidth?: number;
  actionButtonText?: string;
  actionButtonDisabled?: boolean;
  actionButtonColor?: ButtonProps['color'];
  actionButtonVariant?: ButtonProps['variant'];
  onActionButtonClick?: () => void;
  closeOnBackdropClick?: boolean;
  variant?: 'default' | 'error';
};

const Dialog = ({
  open,
  onClose,
  onCancelButtonClick,
  onConfirmButtonClick,
  isLoading,
  titleIcon,
  title,
  titleChip,
  message,
  secondaryMessage,
  children,
  exitButton = true,
  cancelText = 'Cancel',
  confirmText,
  confirmDisabled,
  hideCancelButton = false,
  hideConfirmButton = false,
  graphic,
  footerMessage,
  footerRight,
  footerDivider = true,
  customWidth,
  actionButtonText,
  actionButtonDisabled,
  onActionButtonClick,
  actionButtonColor = 'secondary',
  actionButtonVariant = 'text',
  closeOnBackdropClick = true,
  variant = 'default',
}: Props) => (
  <Wrapper
    open={open}
    onClose={(_, reason) => {
      if (!closeOnBackdropClick && reason === 'backdropClick') {
        return;
      }
      onClose();
    }}
    $customWidth={customWidth}
    maxWidth={customWidth ? false : undefined}
  >
    <Title>
      <Flex sx={{ gap: 1, alignItems: 'center' }}>
        {titleIcon && titleIcon}
        <Typography
          sx={{ fontWeight: componentToken('dialog.title.fontWeight') }}
        >
          {title}
        </Typography>
        {titleChip}
      </Flex>

      {exitButton && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Title>

    <Content>
      {graphic}

      <FlexColumn sx={{ gap: 1 }}>
        {typeof message === 'string' ? (
          <Typography
            sx={{ fontSize: componentToken('dialog.message.fontSize') }}
          >
            {message}
          </Typography>
        ) : (
          message
        )}

        {secondaryMessage && typeof secondaryMessage === 'string' ? (
          <Typography
            sx={{
              fontSize: componentToken('dialog.message.fontSize'),
              color: componentToken('dialog.message.color'),
            }}
          >
            {secondaryMessage}
          </Typography>
        ) : (
          secondaryMessage
        )}
      </FlexColumn>

      {children}
    </Content>

    <Footer $showDivider={footerDivider}>
      {footerRight ? (
        <Flex sx={{ flex: 1, alignItems: 'center' }}>{footerRight}</Flex>
      ) : (
        <>
          {footerMessage && (
            <OverflowTooltip
              color={componentToken('dialog.message.color')}
              sx={{ fontSize: componentToken('dialog.message.fontSize') }}
            >
              {footerMessage}
            </OverflowTooltip>
          )}
          {actionButtonText && (
            <ButtonBase
              color={actionButtonColor}
              variant={actionButtonVariant}
              onClick={onActionButtonClick}
              disabled={actionButtonDisabled}
            >
              {actionButtonText}
            </ButtonBase>
          )}
          <span style={{ flex: 1 }} />
        </>
      )}

      <ButtonContainer>
        {!hideCancelButton && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              (onCancelButtonClick ?? onClose)();
            }}
          >
            {cancelText}
          </Button>
        )}
        {!hideConfirmButton && (
          <LoadingButton
            onClick={(e) => {
              e.stopPropagation();
              onConfirmButtonClick?.();
            }}
            loading={isLoading}
            disabled={confirmDisabled}
            color={variant === 'error' ? 'error' : 'primary'}
          >
            {confirmText}
          </LoadingButton>
        )}
      </ButtonContainer>
    </Footer>
  </Wrapper>
);

export default Dialog;
